import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PLATFORM } from '../constants/platform.constants';
import { AuthService } from '../services/auth.service';

const RETRY_ATTEMPTED_HEADER = 'X-Retry-Attempted';
const INVALID_CREDENTIALS_CODE = 'invalid-credentials-exception';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private injector: Injector) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    try {
      // Si la petición ya tiene el token en los headers, no agregarlo de nuevo
      if (req.headers.has('fb-token')) {
        return next.handle(req).pipe(
          catchError((err) => this.handleAuthError(req, next, err))
        );
      }

      const authService = this.injector.get(AuthService);
      const currentUser = authService.currentUser();

      // Solo agregar el token si existe y la petición es a nuestra API
      if (currentUser?.firebaseToken && this.shouldAddToken(req)) {
        const modifiedReq = this.addTokenToRequest(req, currentUser.firebaseToken, currentUser.buildingId);
        return next.handle(modifiedReq).pipe(
          catchError((err) => this.handleAuthError(req, next, err))
        );
      }

      return next.handle(req);
    } catch (error) {
      return next.handle(req);
    }
  }

  /**
   * Si el backend responde 401 o 406 (invalid-credentials), intenta renovar el token
   * con Firebase (getIdToken(true)) y reintenta la petición una sola vez.
   */
  private handleAuthError(
    originalReq: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    const alreadyRetried = originalReq.headers.has(RETRY_ATTEMPTED_HEADER);
    const isInvalidCredentials = this.isInvalidCredentialsError(error);

    if (!isInvalidCredentials || alreadyRetried) {
      return throwError(() => error);
    }

    const authService = this.injector.get(AuthService);

    return from(authService.hasActiveFirebaseSession()).pipe(
      switchMap((hasSession: boolean) => {
        if (!hasSession) {
          return throwError(() => error);
        }
        return from(authService.refreshFirebaseToken()).pipe(
          switchMap((newToken: string | null) => {
            if (!newToken) {
              return throwError(() => error);
            }
            const currentUser = authService.currentUser();
            const retryReq = this.addTokenToRequest(originalReq, newToken, currentUser?.buildingId)
              .clone({
                setHeaders: { [RETRY_ATTEMPTED_HEADER]: 'true' }
              });
            return next.handle(retryReq).pipe(
              catchError((retryErr) => throwError(() => retryErr))
            );
          })
        );
      }),
      catchError(() => throwError(() => error))
    );
  }

  private isInvalidCredentialsError(error: HttpErrorResponse): boolean {
    if (error.status === 401) {
      return true;
    }
    if (error.status !== 406) {
      return false;
    }
    const code = error.error?.error?.code ?? error.error?.code ?? '';
    const message = error.error?.error?.message ?? error.error?.message ?? '';
    return (
      String(code).includes(INVALID_CREDENTIALS_CODE) ||
      String(message).includes(INVALID_CREDENTIALS_CODE)
    );
  }

  private addTokenToRequest(
    req: HttpRequest<any>,
    token: string,
    buildingId?: string
  ): HttpRequest<any> {
    const headers: { [key: string]: string } = {
      'fb-token': token,
      'x-platform': PLATFORM.WEB,
    };
    if (buildingId) {
      headers['buildingId'] = buildingId;
    }
    return req.clone({ setHeaders: headers });
  }

  // Determina si se debe agregar el token a la petición
  private shouldAddToken(req: HttpRequest<any>): boolean {
    const url = req.url;
    
    // Endpoints que SÍ necesitan el token aunque estén en /auth/
    const authEndpointsThatNeedToken = ['/auth/user-session'];
    if (authEndpointsThatNeedToken.some(endpoint => url.includes(endpoint))) {
      return true;
    }
    
    // No agregar token a peticiones de autenticación o externas
    if (url.includes('/auth/') || url.includes('firebase') || url.includes('googleapis') || url.includes('ui-avatars')) {
      return false;
    }

    // Solo agregar token a peticiones de nuestra API
    return url.includes('/api/') || url.includes('/invitations/');
  }
}
